import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';
import {
  applyLscNeedsAttentionAction,
  getLscArchiveAutopublishSettings,
  listLscNeedsAttention,
  LscApiError,
} from '../api';
import CommissionerOnlyGate from '../components/CommissionerOnlyGate';
import EmptyState from '../components/EmptyState';
import ErrorBanner from '../components/ErrorBanner';
import LoadingState from '../components/LoadingState';
import NeedsAttentionActionConfirmModal from '../components/NeedsAttentionActionConfirmModal';
import NeedsAttentionMediaPreview from '../components/NeedsAttentionMediaPreview';
import NeedsAttentionReassignPanel from '../components/NeedsAttentionReassignPanel';
import BroadcasterSideConfirmationPanel from '../components/BroadcasterSideConfirmationPanel';
import { ReportIssueTrigger } from '../components/support/ReportIssueTrigger';
import { LscAuthGate } from '../hooks/useLscAuthGate';
import { useLeague } from '../hooks/useLeague';
import { lscPath, lscReturnTo } from '../routing';
import { displaySeasonLabel } from '../seasonDisplay';
import type {
  LscConfidenceBand,
  LscNeedsAttentionAction,
  LscNeedsAttentionItem,
} from '../types';

const CONFIDENCE_BADGE_CLASS: Record<LscConfidenceBand, string> = {
  strong: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40',
  needs_review: 'bg-amber-500/15 text-amber-300 border-amber-500/40',
  weak: 'bg-orange-500/15 text-orange-300 border-orange-500/40',
  insufficient: 'bg-rose-500/15 text-rose-300 border-rose-500/40',
};

// Actions that require a final commissioner confirmation before any request is
// sent. Copy is intentionally generic and commissioner-safe (no item internals).
type ConfirmableAction = 'dismiss' | 'approve_to_archive';

const ACTION_CONFIRM_COPY: Record<
  ConfirmableAction,
  { title: string; body: string; confirmLabel: string; destructive: boolean }
> = {
  dismiss: {
    title: 'Remove from Needs Attention?',
    body:
      'This will permanently remove this item from Needs Attention and prevent LSC from ' +
      'archiving it. The original Twitch VOD will not be deleted.',
    confirmLabel: 'Remove item',
    destructive: true,
  },
  approve_to_archive: {
    title: 'Post to Archive?',
    body:
      'This will publish this game to the league archive for members to view. Please confirm ' +
      'the matchup and stream details are correct.',
    confirmLabel: 'Post to Archive',
    destructive: false,
  },
};

const LscNeedsAttentionPage: React.FC = () => {
  const { leagueId } = useParams<{ leagueId: string }>();
  return (
    <LscAuthGate returnTo={lscReturnTo(`/leagues/${leagueId}/needs-attention`)}>
      <CommissionerOnlyGate>
        <NeedsAttentionContent />
      </CommissionerOnlyGate>
    </LscAuthGate>
  );
};

function NeedsAttentionAutomationFooter({ leagueId }: { leagueId: string }) {
  const { isOwner } = useLeague();

  if (isOwner) {
    return (
      <p className="text-xs text-secondary-500" data-testid="needs-attention-automation-footer">
        Looking for automation settings?{' '}
        <Link
          to={lscPath(`/leagues/${leagueId}/settings/archive-automation`)}
          className="text-primary-300"
        >
          Archive automation settings
        </Link>
      </p>
    );
  }

  return (
    <p className="text-xs text-secondary-500" data-testid="needs-attention-automation-footer">
      Archive automation settings are managed by the league owner.
    </p>
  );
}

function NeedsAttentionContent() {
  const { leagueId } = useParams<{ leagueId: string }>();
  const [items, setItems] = useState<LscNeedsAttentionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actingKey, setActingKey] = useState<string | null>(null);
  const [expandedKeys, setExpandedKeys] = useState<Record<string, boolean>>({});
  const [reassignKey, setReassignKey] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<{
    item: LscNeedsAttentionItem;
    action: ConfirmableAction;
  } | null>(null);
  const [publishNote, setPublishNote] = useState('');
  const [autopublishEnabled, setAutopublishEnabled] = useState(false);

  const load = useCallback(async () => {
    if (!leagueId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await listLscNeedsAttention(leagueId);
      setItems(data);
    } catch (err) {
      setError(err instanceof LscApiError ? err.message : 'Could not load Needs Attention queue.');
    } finally {
      setLoading(false);
    }
  }, [leagueId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!leagueId) return;
    let cancelled = false;
    void getLscArchiveAutopublishSettings(leagueId)
      .then((settings) => {
        if (!cancelled) {
          setAutopublishEnabled(Boolean(settings?.autopublish_enabled));
        }
      })
      .catch(() => {
        if (!cancelled) setAutopublishEnabled(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leagueId]);

  const runAction = async (
    item: LscNeedsAttentionItem,
    action: LscNeedsAttentionAction,
    note?: string
  ) => {
    if (!leagueId) return;
    if (action === 'edit_matchup') {
      setReassignKey((prev) => (prev === item.item_key ? null : item.item_key));
      return;
    }

    // Every item type (VOD/handoff review, live review, schedule-lag) carries a
    // stable item_key. Live items keep their "live:" prefix unchanged. Guard
    // against a missing key so we never send an empty action request.
    const itemKey = (item.item_key ?? '').trim();
    if (!itemKey) {
      setActionError('This item is missing a reference. Refresh and try again.');
      return;
    }

    setActingKey(itemKey);
    setActionError(null);
    try {
      await applyLscNeedsAttentionAction(leagueId, {
        item_key: itemKey,
        action,
        note: note?.trim() || undefined,
      });
      await load();
    } catch (err) {
      setActionError(
        err instanceof LscApiError ? err.message : 'Could not complete that action.'
      );
    } finally {
      setActingKey(null);
    }
  };

  // Protected actions (dismiss/reject, approve/post) open a confirmation modal
  // first. The exact existing request is only sent once the commissioner
  // confirms, preserving the Content-Type and loading/error/success behavior.
  const requestAction = (item: LscNeedsAttentionItem, action: ConfirmableAction) => {
    setPublishNote('');
    setPendingAction({ item, action });
  };

  const confirmPendingAction = async () => {
    if (!pendingAction) return;
    const { item, action } = pendingAction;
    const note = action === 'approve_to_archive' ? publishNote : undefined;
    await runAction(item, action, note);
    setPublishNote('');
    setPendingAction(null);
  };

  if (loading) return <LoadingState />;
  if (error) return <ErrorBanner message={error} />;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">Needs Attention</h1>
        <p className="text-sm text-secondary-400 mt-1" data-testid="needs-attention-intro">
          {autopublishEnabled
            ? 'Games that need your review before they can archive automatically. Strong matches archive on autopilot; uncertain games land here.'
            : 'Games that need your review before they can be archived. Automatic publishing is off, so a strong match still waits here for you.'}
        </p>
      </div>

      {actionError ? (
        <div className="space-y-2" data-testid="needs-attention-action-error">
          <ErrorBanner message={actionError} />
          <ReportIssueTrigger
            variant="inline"
            testId="needs-attention-report-issue"
            prefill={{
              leagueId,
              surface: 'needs_attention_page',
              category: 'vod_handoff',
              title: 'Needs Attention issue',
              description: actionError,
            }}
          />
        </div>
      ) : null}

      {items.length === 0 ? (
        <EmptyState
          title="Nothing needs attention"
          message="Verified games with strong evidence archive automatically. Uncertain games will appear here."
        />
      ) : (
        <div className="lsc-split-when-wide">
        <ul className="lsc-split-grid-loose" data-testid="needs-attention-list">
          {items.map((item) => {
            const expanded = Boolean(expandedKeys[item.item_key]);
            return (
              <li key={item.item_key} className="card space-y-4" data-testid="needs-attention-card">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      {item.is_live_exception ? (
                        <span
                          className="inline-flex items-center gap-1.5 rounded-full border border-rose-500/40 bg-rose-500/15 px-2 py-0.5 text-xs font-medium text-rose-200"
                          data-testid="needs-attention-live-badge"
                        >
                          <span className="h-1.5 w-1.5 rounded-full bg-rose-400" aria-hidden />
                          Live now
                        </span>
                      ) : null}
                      <h2 className="text-lg font-semibold text-white">{item.matchup_label}</h2>
                    </div>
                    {item.is_live_exception && item.status_label ? (
                      <p
                        className="mt-1 text-xs font-medium text-amber-300"
                        data-testid="needs-attention-status-label"
                      >
                        {item.status_label}
                      </p>
                    ) : null}
                    <p className="text-sm text-secondary-400 mt-1">
                      {[item.week_label, displaySeasonLabel(item.season_label)].filter(Boolean).join(' · ') ||
                        'Schedule details unavailable'}
                      {' · '}
                      {item.streamer_display}
                    </p>
                  </div>
                  {typeof item.confidence_score === 'number' ? (
                    <div
                      className={`shrink-0 rounded-lg border px-3 py-1.5 text-right ${
                        CONFIDENCE_BADGE_CLASS[item.confidence_band ?? 'insufficient']
                      }`}
                      data-testid="needs-attention-confidence"
                    >
                      <div className="text-lg font-bold leading-none">
                        {item.confidence_score}
                        <span className="text-xs font-normal opacity-70"> / 100</span>
                      </div>
                      <div className="mt-1 text-xs font-medium">
                        {item.confidence_band_label ?? 'Not enough evidence'}
                      </div>
                    </div>
                  ) : null}
                </div>

                <NeedsAttentionMediaPreview
                  playback={item.playback}
                  matchupLabel={item.matchup_label}
                />

                <div className="space-y-2">
                  <p className="text-sm text-secondary-200">{item.exception_reason}</p>
                  <p className="text-sm text-secondary-400">{item.recommended_action}</p>
                  {item.candidate_review_path && leagueId ? (
                    <Link
                      to={lscPath(item.candidate_review_path)}
                      className="text-sm text-primary-300"
                      data-testid="needs-attention-candidate-link"
                    >
                      Open the broadcast
                    </Link>
                  ) : null}
                </div>

                {item.evidence_summary?.live_game_side_pending &&
                item.evidence_summary?.side_confirmation?.available &&
                leagueId ? (
                  <BroadcasterSideConfirmationPanel
                    leagueId={leagueId}
                    homeTeamLabel={item.evidence_summary.side_confirmation.home_team_label}
                    awayTeamLabel={item.evidence_summary.side_confirmation.away_team_label}
                    needsAttentionItemKey={item.item_key}
                    onConfirmed={load}
                    testIdPrefix={`needs-attention-side-confirmation-${item.item_key}`}
                  />
                ) : null}

                <div>
                  <button
                    type="button"
                    className="text-sm text-primary-300 hover:text-primary-200"
                    onClick={() =>
                      setExpandedKeys((prev) => ({
                        ...prev,
                        [item.item_key]: !prev[item.item_key],
                      }))
                    }
                  >
                    {expanded ? 'Hide evidence summary' : 'Show evidence summary'}
                  </button>
                  {expanded && item.evidence_summary ? (
                    <dl
                      className="mt-3 grid gap-2 text-sm"
                      data-testid="needs-attention-evidence"
                    >
                      {Object.entries(item.evidence_summary).map(([key, value]) => (
                        <div key={key} className="flex justify-between gap-4">
                          <dt className="text-secondary-500 capitalize">
                            {key.replace(/_/g, ' ')}
                          </dt>
                          <dd className="text-secondary-200">{String(value)}</dd>
                        </div>
                      ))}
                    </dl>
                  ) : null}
                </div>

                <div className="flex flex-wrap gap-2">
                  {item.available_actions.includes('approve_to_archive') ? (
                    <button
                      type="button"
                      className="btn-primary text-sm"
                      disabled={actingKey === item.item_key}
                      onClick={() => requestAction(item, 'approve_to_archive')}
                    >
                      {item.is_live_exception ? 'Approve as live league game' : 'Approve to archive'}
                    </button>
                  ) : null}
                  {item.available_actions.includes('edit_matchup') ? (
                    <button
                      type="button"
                      className="btn-secondary text-sm"
                      disabled={actingKey === item.item_key}
                      onClick={() => void runAction(item, 'edit_matchup')}
                    >
                      {item.is_live_exception ? 'Choose game' : 'Edit matchup'}
                    </button>
                  ) : null}
                  {item.available_actions.includes('dismiss') ? (
                    <button
                      type="button"
                      className="btn-secondary text-sm"
                      disabled={actingKey === item.item_key}
                      onClick={() => requestAction(item, 'dismiss')}
                    >
                      Dismiss
                    </button>
                  ) : null}
                </div>

                {reassignKey === item.item_key && leagueId ? (
                  <NeedsAttentionReassignPanel
                    leagueId={leagueId}
                    item={item}
                    onCancel={() => setReassignKey(null)}
                    onDone={() => {
                      setReassignKey(null);
                      void load();
                    }}
                  />
                ) : null}
              </li>
            );
          })}
        </ul>
        </div>
      )}

      {leagueId ? <NeedsAttentionAutomationFooter leagueId={leagueId} /> : null}

      <NeedsAttentionActionConfirmModal
        open={pendingAction !== null}
        title={pendingAction ? ACTION_CONFIRM_COPY[pendingAction.action].title : ''}
        body={pendingAction ? ACTION_CONFIRM_COPY[pendingAction.action].body : ''}
        confirmLabel={pendingAction ? ACTION_CONFIRM_COPY[pendingAction.action].confirmLabel : ''}
        destructive={pendingAction ? ACTION_CONFIRM_COPY[pendingAction.action].destructive : false}
        showNote={pendingAction?.action === 'approve_to_archive'}
        confirm={
          pendingAction?.action === 'approve_to_archive'
            ? pendingAction.item.publish_confirm
            : null
        }
        note={publishNote}
        onNoteChange={setPublishNote}
        busy={actingKey !== null}
        onConfirm={() => void confirmPendingAction()}
        onCancel={() => {
          setPublishNote('');
          setPendingAction(null);
        }}
      />
    </div>
  );
}

export default LscNeedsAttentionPage;
